import { calculateDecoratingQuote } from '../quote/calculate';
import { validateRoom } from '../quote/eligibility';
import type { Locale, Market, Region, RoomInput, Travel } from '../quote/types';
import {
  copy,
  createRoom,
  newId,
  numberValue,
  quoteInput,
  state,
  type PropertyType,
  type Step
} from './model';
import { renderHeader, renderHero, renderProgress } from './shell';
import {
  renderConditionStep,
  renderDimensionsStep,
  renderFinishStep,
  renderProjectStep,
  renderSurfacesStep
} from './steps';

function renderCurrentStep(): string {
  switch (state.step) {
    case 0: return renderProjectStep();
    case 1: return renderDimensionsStep();
    case 2: return renderSurfacesStep();
    case 3: return renderConditionStep();
    case 4: return renderFinishStep();
  }
}

function renderActions(): string {
  const c = copy();
  return `
    <div class="wizard-actions">
      <button type="button" class="button secondary" data-action="back" ${state.step === 0 ? 'disabled' : ''}>← ${c.back}</button>
      <div class="action-right">
        <button type="button" class="text-button reset" data-action="reset">${c.reset}</button>
        ${state.step < 4
          ? `<button type="button" class="button primary" data-action="next">${c.next} →</button>`
          : `<button type="button" class="button primary" data-action="calculate">${c.calculate}</button>`}
      </div>
    </div>`;
}

function render(root: HTMLElement): void {
  const c = copy();
  document.documentElement.lang = state.locale;
  document.title = `${c.title} — MKProjectLab`;
  root.innerHTML = `
    ${renderHeader()}
    <main>
      ${renderHero()}
      <section class="calculator-shell" id="calculator">
        ${renderProgress()}
        <div class="wizard-content">
          ${state.errors.size ? `<div class="error-summary" role="alert" tabindex="-1"><strong>${c.errorsHeading}</strong></div>` : ''}
          ${renderCurrentStep()}
          ${renderActions()}
        </div>
      </section>
      <section class="privacy-strip"><strong>${c.conceptLabel}</strong><span>${c.ctaNote}</span></section>
    </main>
    <footer><span>© ${new Date().getFullYear()} MKProjectLab</span><a href="mailto:contact@mkprojectlab.com">contact@mkprojectlab.com</a></footer>`;
}

function findRoom(id: string): RoomInput | undefined {
  return state.rooms.find((room) => room.id === id);
}

function validateStep(step: Step): boolean {
  state.errors = new Map();
  const c = copy();

  if (step >= 1) {
    for (const room of state.rooms) {
      const errors = validateRoom(room);
      for (const field of errors) {
        if (step === 1 && ['length', 'width', 'height', 'doors', 'windows', 'radiators'].includes(field)) {
          state.errors.set(`${room.id}:${field}`, c.invalidNumber);
        }
        if (step >= 2 && field === 'surfaces') state.errors.set(`${room.id}:surfaces`, c.selectSurface);
        if (step >= 2 && ['paintedDoors', 'paintedRadiators'].includes(field)) state.errors.set(`${room.id}:${field}`, c.paintedCount);
      }
    }
  }

  return state.errors.size === 0;
}

function setMarket(market: Market): void {
  state.market = market;
  state.locale = market === 'UK' ? 'en-GB' : 'pl-PL';
  state.region = 'standard';
  state.result = null;
}

function updateRoomField(room: RoomInput, field: string, target: HTMLInputElement | HTMLSelectElement): void {
  const booleanFields = ['paintWalls', 'paintCeiling', 'paintSkirting', 'wallpaperRemoval', 'stainBlocking', 'highCeiling', 'difficultAccess', 'furnished'];
  const numberFields = ['length', 'width', 'height', 'doors', 'windows', 'radiators', 'paintedDoors', 'paintedRadiators'];

  if (booleanFields.includes(field) && target instanceof HTMLInputElement) {
    (room as unknown as Record<string, unknown>)[field] = target.checked;
  } else if (numberFields.includes(field)) {
    (room as unknown as Record<string, unknown>)[field] = numberValue(target.value);
  } else {
    (room as unknown as Record<string, unknown>)[field] = target.value;
  }
  state.result = null;
}

function mountEvents(root: HTMLElement): void {
  root.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    const action = target.dataset.action;
    if (action === 'locale') {
      state.locale = target.value as Locale;
      render(root);
      return;
    }
    if (action === 'market') {
      setMarket(target.value as Market);
      render(root);
      return;
    }
    if (action === 'property-type') state.propertyType = target.value as PropertyType;
    if (action === 'region') state.region = target.value as Region;
    if (action === 'travel') state.travel = target.value as Travel;
    if (action === 'materials') {
      for (const room of state.rooms) room.materialStandard = target.value as RoomInput['materialStandard'];
      state.result = null;
    }

    const roomId = target.dataset.roomId;
    const field = target.dataset.field;
    const issue = target.dataset.issue;
    const room = roomId ? findRoom(roomId) : undefined;
    if (room && field) updateRoomField(room, field, target);
    if (room && issue && target instanceof HTMLInputElement) {
      room.issues[issue as keyof RoomInput['issues']] = target.checked;
      state.result = null;
    }

    render(root);
  });

  root.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type === 'checkbox' || target.type === 'radio') return;
    const room = target.dataset.roomId ? findRoom(target.dataset.roomId) : undefined;
    const field = target.dataset.field;
    if (!room || !field) return;
    updateRoomField(room, field, target);
  });

  root.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!button || button instanceof HTMLInputElement || button instanceof HTMLSelectElement) return;
    const action = button.dataset.action;

    if (action === 'next') {
      if (!validateStep(state.step)) {
        render(root);
        root.querySelector<HTMLElement>('.error-summary')?.focus();
        return;
      }
      state.step = Math.min(4, state.step + 1) as Step;
      state.errors.clear();
      render(root);
      root.querySelector('#calculator')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (action === 'back') {
      state.step = Math.max(0, state.step - 1) as Step;
      state.errors.clear();
      state.result = null;
      render(root);
    }

    if (action === 'go-step') {
      const step = Number(button.dataset.step) as Step;
      if (step <= state.step) {
        state.step = step;
        state.result = null;
        state.errors.clear();
        render(root);
      }
    }

    if (action === 'add-room' && state.rooms.length < 5) {
      state.rooms.push(createRoom(state.rooms.length));
      render(root);
    }

    if (action === 'duplicate-room') {
      const room = button.dataset.roomId ? findRoom(button.dataset.roomId) : undefined;
      if (room && state.rooms.length < 5) {
        state.rooms.push({ ...structuredClone(room), id: newId(), customName: room.customName ? `${room.customName} copy` : '' });
        render(root);
      }
    }

    if (action === 'remove-room') {
      state.rooms = state.rooms.filter((room) => room.id !== button.dataset.roomId);
      render(root);
    }

    if (action === 'reset' && window.confirm(copy().resetConfirm)) {
      Object.assign(state, {
        locale: state.market === 'UK' ? 'en-GB' : 'pl-PL',
        region: 'standard',
        travel: 'local',
        propertyType: 'house',
        step: 0,
        rooms: [createRoom()],
        result: null,
        errors: new Map()
      });
      render(root);
    }

    if (action === 'calculate') {
      if (!validateStep(4)) {
        render(root);
        return;
      }
      state.result = calculateDecoratingQuote(quoteInput());
      render(root);
      root.querySelector<HTMLElement>('.result-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}

export function mountCalculator(root: HTMLElement): void {
  render(root);
  mountEvents(root);
}
